import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiEdit2,
  FiExternalLink,
  FiLink,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiRotateCw,
  FiSave,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import { formatDateTime } from './inquiriesShared';
import AppointmentDateTimeRangeField from './AppointmentDateTimeRangeField';
import LocationInput from './LocationInput';
import { getLocationAddress, normalizeLocationValue } from './locationUtils';
import './InquiryCalendarPage.css';
import GoogleMeetSection from '../components/OnlineMeetingSection';

const monthLabelFormatter = new Intl.DateTimeFormat('nb-NO', {
  month: 'long',
  year: 'numeric',
});

const dayLabelFormatter = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'short',
  day: '2-digit',
});

const weekdayFormatter = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'short',
});

const statusOptions = [
  { value: 'planned', label: 'Planlagt' },
  { value: 'done', label: 'Ferdig' },
  { value: 'todo', label: 'Ikke planlagt' },
];

const pad = (value) => String(value).padStart(2, '0');

const getLocalDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const getMonthStart = (value) => {
  const source = value instanceof Date ? new Date(value) : new Date(value || Date.now());
  if (Number.isNaN(source.getTime())) return getMonthStart(new Date());
  source.setDate(1);
  source.setHours(0, 0, 0, 0);
  return source;
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const addMonths = (date, months) => {
  const copy = getMonthStart(date);
  copy.setMonth(copy.getMonth() + months);
  return copy;
};

const getCalendarGridStart = (monthStart) => {
  const date = getMonthStart(monthStart);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(date, diff);
};

const getMonthEnd = (monthStart) => {
  const date = getMonthStart(monthStart);
  date.setMonth(date.getMonth() + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

const getCalendarGridEnd = (monthStart) => {
  const monthEnd = getMonthEnd(monthStart);
  const day = monthEnd.getDay();
  const diff = day === 0 ? 0 : 7 - day;
  const date = addDays(monthEnd, diff);
  date.setHours(23, 59, 59, 999);
  return date;
};

const formatDateTimeInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const inputDateTimeToIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const getEventStart = (event) => event?.planned_start_at || event?.planned_end_at || null;

const getEventEnd = (event) => event?.planned_end_at || event?.planned_start_at || null;

const getInquiryName = (event) => (
  event?.inquiry?.display_key
  || event?.inquiry?.displayKey
  || event?.inquiry?.display_name
  || event?.inquiry?.subject
  || `Henvendelse #${event?.inbox_id || ''}`.trim()
);

const getAssigneeLabel = (event) => (
  event?.assigned_member?.display_name
  || event?.assigned_member?.email
  || 'Ufordelt'
);

const getInitials = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '?';
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ''}${parts[parts.length - 1]?.[0] || ''}`.toUpperCase();
};

const resolveProfileImageUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    return new URL(trimmed, DashboardConfig.apiBaseUrl).href;
  } catch {
    return trimmed;
  }
};

const getAssigneeProfileImageUrl = (event) => {
  const member = event?.assigned_member || {};
  return resolveProfileImageUrl(
    member.profilePictureUrl
    || member.profile_picture_url
    || member.avatarUrl
    || member.avatar_url
    || member.photoUrl
    || member.photo_url
    || null,
  );
};

const formatTimeOnly = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAppointmentAttendanceType = (metadata = {}) => {
  const value = String(metadata?.attendance_type || metadata?.meeting_type || '').trim().toLowerCase();
  return ['digital', 'physical'].includes(value) ? value : '';
};

const getAppointmentLocationAddress = (metadata = {}) => {
  const location = metadata?.location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return '';
  return getLocationAddress(location);
};

const buildGoogleMapsUrl = (location) => {
  const normalized = normalizeLocationValue(location);
  if (!normalized) return null;
  if (Number.isFinite(Number(normalized.lat)) && Number.isFinite(Number(normalized.lng))) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${normalized.lat},${normalized.lng}`)}${
      normalized.place_id ? `&query_place_id=${encodeURIComponent(normalized.place_id)}` : ''
    }`;
  }
  const address = getLocationAddress(normalized);
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}${
    normalized.place_id ? `&query_place_id=${encodeURIComponent(normalized.place_id)}` : ''
  }`;
};

const createDraftFromEvent = (event) => {
  const taskAssignedId = event?.task_assigned_member?.account_id || null;
  const inquiryAssignedId = event?.inquiry_assigned_member?.account_id || null;
  const metadata = event?.metadata || {};
  return {
    status: event?.status || 'planned',
    plannedStartAt: formatDateTimeInputValue(event?.planned_start_at),
    plannedEndAt: formatDateTimeInputValue(event?.planned_end_at),
    assignedAccountId: taskAssignedId && taskAssignedId !== inquiryAssignedId ? taskAssignedId : '',
    comment: typeof event?.metadata?.comment === 'string' ? event.metadata.comment : '',
    attendanceType: getAppointmentAttendanceType(metadata),
    meetingUrl: typeof metadata.meeting_url === 'string' ? metadata.meeting_url : '',
    locationAddress: getAppointmentLocationAddress(metadata),
    locationValue: normalizeLocationValue(metadata.location || null),
    useGoogleMeet: metadata.use_google_meet === true || false,
    useMicrosoftTeams: metadata.use_microsoft_teams === true || false,
    googleImpersonatedEmail: typeof metadata.google_impersonated_email === 'string' ? metadata.google_impersonated_email : '',
    meetingGuests: Array.isArray(metadata.google_meet_guests) ? metadata.google_meet_guests : [],
    meetingDescription: typeof metadata.google_meet_description === 'string' ? metadata.google_meet_description : '',
    meetingProvider: metadata.use_google_meet ? 'google'
      : metadata.use_microsoft_teams ? 'microsoft'
      : null,
    googleMeetUrl: typeof metadata.google_meet_url === 'string' ? metadata.google_meet_url : '',
    microsoftTeamsUrl: typeof metadata.microsoft_teams_url === 'string' ? metadata.microsoft_teams_url : '',
  };
};

const getEventKey = (event) => `${event?.inbox_id || 'inbox'}:${event?.step_key || 'step'}`;

const defaultFeedForm = {
  name: 'Min kundeløp-kalender',
  scope: 'assigned_to_me',
  botProductId: '',
  enabled: true,
  excludeGoogleMeet: true,
};

const getCalendarFeedUrl = (feed) => {
  if (!feed?.token) return '';
  return `${DashboardConfig.apiBaseUrl.replace(/\/$/, '')}/api/dashboard/calendar/ical/${encodeURIComponent(feed.token)}`;
};

const getFeedProductId = (feed) => (
  feed?.botProductId
  ?? feed?.bot_product_id
  ?? feed?.product?.id
  ?? ''
);

const getFeedScopeLabel = (feed) => (
  feed?.scope === 'product'
    ? `Produkt${feed?.product?.name ? `: ${feed.product.name}` : ''}`
    : 'Tildelt meg'
);

const CalendarEventCard = ({ event, onOpen }) => {
  const product = event?.product || {};
  const inquiry = event?.inquiry || {};
  const start = getEventStart(event);
  const end = event?.planned_end_at || null;
  const status = event?.status || 'planned';
  const assigneeLabel = getAssigneeLabel(event);
  const profileImageUrl = getAssigneeProfileImageUrl(event);
  const metadata = event?.metadata || {};
  const attendanceType = getAppointmentAttendanceType(metadata);
  const meetingUrl = typeof metadata.meeting_url === 'string' ? metadata.meeting_url.trim() : '';
  const location = normalizeLocationValue(metadata.location || null);
  const locationAddress = getLocationAddress(location);
  const googleMapsUrl = buildGoogleMapsUrl(location);

  return (
    <article
      className={`inquiry-calendar-event is-${status}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(event)}
      onKeyDown={(keyboardEvent) => {
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault();
          onOpen(event);
        }
      }}
    >
      <header>
        <span className="inquiry-calendar-event-product">
          {product.color_code ? (
            <i style={{ backgroundColor: product.color_code }} aria-hidden="true" />
          ) : null}
          {product.name || 'Produkt'}
        </span>
      </header>
      <div className="inquiry-calendar-event-times" aria-label="Tidspunkt">
        <span>
          <small>Fra</small>
          <strong>{formatTimeOnly(start)}</strong>
        </span>
        <span className={!end ? 'is-muted' : ''}>
          <small>Til</small>
          <strong>{end ? formatTimeOnly(end) : '—'}</strong>
        </span>
      </div>
      <div className="inquiry-calendar-event-main">
        <strong>{event?.step_label || event?.step_key}</strong>
        <Link
          to={`/henvendelser/${event?.inbox_id}`}
          title="Åpne henvendelse"
          onClick={(clickEvent) => clickEvent.stopPropagation()}
        >
          {getInquiryName(event)}
          <FiExternalLink aria-hidden="true" />
        </Link>
        <span className="inquiry-calendar-event-assignee">
          <span className="inquiry-calendar-event-avatar" aria-hidden="true">
            {profileImageUrl ? <img src={profileImageUrl} alt="" /> : getInitials(assigneeLabel)}
          </span>
          <small>{assigneeLabel}</small>
        </span>
        {inquiry.email || inquiry.phone || inquiry.subject ? (
          <small>{inquiry.email || inquiry.phone || inquiry.subject}</small>
        ) : null}
        {attendanceType ? (
          <div className="inquiry-calendar-event-attendance">
            <span>{attendanceType === 'digital' ? 'Digitalt' : 'Fysisk'}</span>
            {attendanceType === 'digital' && meetingUrl ? (
              <a
                href={meetingUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(clickEvent) => clickEvent.stopPropagation()}
              >
                {meetingUrl}
                <FiExternalLink aria-hidden="true" />
              </a>
            ) : null}
            {attendanceType === 'physical' && locationAddress ? (
              <a
                href={googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationAddress)}`}
                target="_blank"
                rel="noreferrer"
                onClick={(clickEvent) => clickEvent.stopPropagation()}
              >
                <FiMapPin aria-hidden="true" />
                {locationAddress}
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

const InquiryCalendarPage = () => {
  const { selectedCompanyId, selectedCompany, permissions } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  const canView = Boolean(permissions?.canViewInbox);
  const canManage = Boolean(permissions?.canManageInbox || permissions?.canEditBots);

  const [monthStart, setMonthStart] = useState(() => getMonthStart(new Date()));
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [botProductFilter, setBotProductFilter] = useState('');
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [calendarFeeds, setCalendarFeeds] = useState([]);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const [feedSaving, setFeedSaving] = useState(false);
  const [feedActionId, setFeedActionId] = useState(null);
  const [feedFormOpen, setFeedFormOpen] = useState(false);
  const [editingFeedId, setEditingFeedId] = useState(null);
  const [feedForm, setFeedForm] = useState(defaultFeedForm);
  const [, setError] = useState(null);
  const [hasGoogleServiceAccount, setHasGoogleServiceAccount] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    DashboardApi.getGoogleServiceAccount(companyId)
      .then((data) => setHasGoogleServiceAccount(Boolean(data?.configured)))
      .catch(() => setHasGoogleServiceAccount(false));
  }, [companyId]);

  const gridStart = useMemo(() => getCalendarGridStart(monthStart), [monthStart]);
  const gridEnd = useMemo(() => getCalendarGridEnd(monthStart), [monthStart]);
  const calendarDays = useMemo(() => {
    const days = [];
    let current = new Date(gridStart);
    while (current <= gridEnd) {
      days.push(new Date(current));
      current = addDays(current, 1);
    }
    return days;
  }, [gridEnd, gridStart]);
  const monthLabel = monthLabelFormatter.format(monthStart);

  const loadProducts = useCallback(async () => {
    if (!companyId) {
      setProducts([]);
      setAssignees([]);
      return;
    }
    setSummaryLoading(true);
    try {
      const data = await DashboardApi.getCompanyInboxSummary(companyId);
      const nextProducts = Array.isArray(data?.products)
        ? data.products
          .filter((product) => (
            product?.product_id !== null
            && product?.product_id !== undefined
          ) || (
            product?.id !== null
            && product?.id !== undefined
          ))
          .map((product) => ({
            id: String(product.product_id ?? product.id),
            name: product.product_name || product.name || product.product || 'Produkt',
            color_code: product.color_code || product.colorCode || null,
          }))
        : [];
      setProducts(nextProducts);
      setAssignees(Array.isArray(data?.assignees) ? data.assignees : []);
    } catch (err) {
      setProducts([]);
      setAssignees([]);
      notifyError(err.message || 'Kunne ikke hente produkter.', {
        title: 'Kunne ikke hente produktfilter',
        source: 'Kundeløp kalender',
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [companyId]);

  const loadEvents = useCallback(async () => {
    if (!companyId) {
      setEvents([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {
        from: gridStart.toISOString(),
        to: gridEnd.toISOString(),
        assigned: assignedFilter,
      };
      if (botProductFilter) params.botProductId = botProductFilter;
      const data = await DashboardApi.getCompanyInboxCalendar(companyId, params);
      setEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (err) {
      setEvents([]);
      const message = err.message || 'Kunne ikke hente kundeløp-kalenderen.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente kalender', source: 'Kundeløp kalender' });
    } finally {
      setLoading(false);
    }
  }, [assignedFilter, botProductFilter, companyId, gridEnd, gridStart]);

  const loadCalendarFeeds = useCallback(async () => {
    if (!companyId) {
      setCalendarFeeds([]);
      return;
    }
    setFeedsLoading(true);
    try {
      const data = await DashboardApi.getCompanyInboxCalendarFeeds(companyId);
      setCalendarFeeds(Array.isArray(data?.feeds) ? data.feeds : []);
    } catch (err) {
      setCalendarFeeds([]);
      notifyError(err.message || 'Kunne ikke hente delte kalendere.', {
        title: 'Kunne ikke hente kalenderlenker',
        source: 'Kundeløp kalender',
      });
    } finally {
      setFeedsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadCalendarFeeds();
  }, [loadCalendarFeeds]);

  const eventsByDay = useMemo(() => {
    const grouped = new Map(calendarDays.map((day) => [getLocalDateKey(day), []]));
    events.forEach((event) => {
      const key = getLocalDateKey(getEventStart(event) || getEventEnd(event));
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(event);
    });
    grouped.forEach((items) => {
      items.sort((left, right) => {
        const leftTime = new Date(getEventStart(left) || getEventEnd(left) || 0).getTime();
        const rightTime = new Date(getEventStart(right) || getEventEnd(right) || 0).getTime();
        return leftTime - rightTime;
      });
    });
    return grouped;
  }, [calendarDays, events]);

  const stats = useMemo(() => {
    const done = events.filter((event) => event.status === 'done').length;
    const planned = events.filter((event) => event.status === 'planned').length;
    const uniqueInquiries = new Set(events.map((event) => event.inbox_id).filter(Boolean)).size;
    return { total: events.length, planned, done, uniqueInquiries };
  }, [events]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return [...events]
      .filter((event) => {
        const start = new Date(getEventStart(event) || getEventEnd(event) || 0);
        return !Number.isNaN(start.getTime()) && start >= now;
      })
      .sort((left, right) => {
        const leftTime = new Date(getEventStart(left) || getEventEnd(left) || 0).getTime();
        const rightTime = new Date(getEventStart(right) || getEventEnd(right) || 0).getTime();
        return leftTime - rightTime;
      })
      .slice(0, 10);
  }, [events]);

  const openEvent = useCallback((event) => {
    setSelectedEvent(event);
    setSelectedDraft(createDraftFromEvent(event));
  }, []);

  const closeEvent = useCallback(() => {
    setSelectedEvent(null);
    setSelectedDraft(null);
  }, []);

  const updateSelectedDraft = useCallback((patch) => {
    setSelectedDraft((current) => ({
      ...(current || {}),
      ...patch,
    }));
  }, []);

  const handleSaveEvent = useCallback(async () => {
    if (!companyId || !selectedEvent?.inbox_id || !selectedEvent?.step_key) return;
    const key = getEventKey(selectedEvent);
    const draft = selectedDraft || createDraftFromEvent(selectedEvent);

    if (draft.meetingProvider === 'google' && !draft.googleImpersonatedEmail) {
      notifyError('Vennligst verifiser e-post for Google Meet før du lagrer.', {
        title: 'Validering feilet',
        source: 'Kundeløp kalender',
      });
      return;
    }

    if (draft.meetingProvider && !draft.plannedStartAt) {
      notifyError('Du må sette en dato for møtet før du kan opprette et videomøte.', {
        title: 'Validering feilet',
        source: 'Kundeløp kalender',
      });
      return;
    }

    if (draft.meetingProvider === 'microsoft') {
      try {
        const ms = await DashboardApi.getMicrosoftStatus();
        if (!ms?.connected) {
          notifyError('Du må koble til Microsoft Teams før du kan opprette videomøter.', {
            title: 'Validering feilet',
            source: 'Kundeløp kalender',
          });
          return;
        }
      } catch {
        notifyError('Kunne ikke sjekke Microsoft-tilkoblingen.', {
          title: 'Validering feilet',
          source: 'Kundeløp kalender',
        });
        return;
      }
    }

    setSavingKey(key);
    try {
      const response = await DashboardApi.updateCompanyInboxProgression(companyId, selectedEvent.inbox_id, selectedEvent.step_key, {
        status: draft.status || 'planned',
        assignedAccountId: draft.assignedAccountId || null,
        plannedStartAt: inputDateTimeToIso(draft.plannedStartAt),
        plannedEndAt: inputDateTimeToIso(draft.plannedEndAt),
        comment: draft.comment || '',
        attendanceType: draft.attendanceType || null,
        meetingUrl: draft.attendanceType === 'digital' && !draft.meetingProvider ? draft.meetingUrl || null : null,
        location: draft.attendanceType === 'physical' && (draft.locationValue || draft.locationAddress)
          ? (normalizeLocationValue(draft.locationValue) || { address: draft.locationAddress })
          : null,
        useGoogleMeet: draft.meetingProvider === 'google',
        useMicrosoftTeams: draft.meetingProvider === 'microsoft',
        googleImpersonatedEmail: draft.meetingProvider === 'google' ? String(draft.googleImpersonatedEmail || '').trim() || null : null,
        googleMeetGuests: draft.meetingProvider ? (Array.isArray(draft.meetingGuests) ? draft.meetingGuests.filter(Boolean) : []) : [],
        googleMeetDescription: draft.meetingProvider ? String(draft.meetingDescription || '').trim() || null : null,
      });
      const savedStep = (response?.inquiry?.progression?.steps || [])
        .find((s) => s?.step_key === selectedEvent?.step_key);
      if (savedStep?.metadata) {
        updateSelectedDraft({
          googleMeetUrl: savedStep.metadata.google_meet_url || '',
          microsoftTeamsUrl: savedStep.metadata.microsoft_teams_url || '',
        });
      }
      notifySuccess('Kalenderavtalen ble oppdatert.', { source: 'Kundeløp kalender' });
      if (response?._googleSyncError) {
        notifyError(response._googleSyncError, { title: 'Google Meet-synkronisering feilet', source: 'Kundeløp kalender' });
      }
      closeEvent();
      await loadEvents();
    } catch (err) {
      notifyError(err.message || 'Kunne ikke oppdatere kalenderavtalen.', {
        title: 'Kunne ikke lagre',
        source: 'Kundeløp kalender',
      });
    } finally {
      setSavingKey(null);
    }
  }, [closeEvent, companyId, loadEvents, selectedDraft, selectedEvent, updateSelectedDraft]);

  const openCreateFeedForm = useCallback(() => {
    setEditingFeedId(null);
    setFeedForm(defaultFeedForm);
    setFeedFormOpen(true);
  }, []);

  const openEditFeedForm = useCallback((feed) => {
    setEditingFeedId(feed.id);
    setFeedForm({
      name: feed.name || defaultFeedForm.name,
      scope: feed.scope || 'assigned_to_me',
      botProductId: getFeedProductId(feed) ? String(getFeedProductId(feed)) : '',
      enabled: feed.enabled !== false,
      excludeGoogleMeet: feed.excludeGoogleMeet !== false,
    });
    setFeedFormOpen(true);
  }, []);

  const closeFeedForm = useCallback(() => {
    setEditingFeedId(null);
    setFeedForm(defaultFeedForm);
    setFeedFormOpen(false);
  }, []);

  const updateFeedForm = useCallback((patch) => {
    setFeedForm((current) => ({
      ...current,
      ...patch,
      ...(patch.scope === 'assigned_to_me' ? { botProductId: '' } : null),
    }));
  }, []);

  const handleSaveFeed = useCallback(async (event) => {
    event.preventDefault();
    if (!companyId) return;
    if (!feedForm.name.trim()) {
      notifyError('Gi kalenderlenken et navn.', { title: 'Mangler navn', source: 'Kundeløp kalender' });
      return;
    }
    if (feedForm.scope === 'product' && !feedForm.botProductId) {
      notifyError('Velg produkt for produktkalenderen.', { title: 'Mangler produkt', source: 'Kundeløp kalender' });
      return;
    }

    setFeedSaving(true);
    try {
      const payload = {
        name: feedForm.name.trim(),
        scope: feedForm.scope,
        botProductId: feedForm.scope === 'product' ? feedForm.botProductId : null,
        enabled: feedForm.enabled !== false,
        excludeGoogleMeet: feedForm.excludeGoogleMeet !== false,
      };
      if (editingFeedId) {
        await DashboardApi.updateCompanyInboxCalendarFeed(companyId, editingFeedId, payload);
        notifySuccess('Kalenderlenken ble oppdatert.', { source: 'Kundeløp kalender' });
      } else {
        await DashboardApi.createCompanyInboxCalendarFeed(companyId, payload);
        notifySuccess('Kalenderlenken ble opprettet.', { source: 'Kundeløp kalender' });
      }
      closeFeedForm();
      await loadCalendarFeeds();
    } catch (err) {
      notifyError(err.message || 'Kunne ikke lagre kalenderlenken.', {
        title: 'Kunne ikke lagre kalenderlenke',
        source: 'Kundeløp kalender',
      });
    } finally {
      setFeedSaving(false);
    }
  }, [closeFeedForm, companyId, editingFeedId, feedForm, loadCalendarFeeds]);

  const copyFeedUrl = useCallback(async (feed) => {
    const url = getCalendarFeedUrl(feed);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      notifySuccess('Kalenderlenken ble kopiert.', { source: 'Kundeløp kalender' });
    } catch (_err) {
      notifyError('Kunne ikke kopiere lenken automatisk.', {
        title: 'Kopiering feilet',
        source: 'Kundeløp kalender',
      });
    }
  }, []);

  const rotateFeed = useCallback(async (feed) => {
    if (!companyId || !feed?.id) return;
    const confirmed = window.confirm('Rotere denne kalenderlenken? Den gamle URL-en slutter å virke.');
    if (!confirmed) return;
    setFeedActionId(feed.id);
    try {
      await DashboardApi.rotateCompanyInboxCalendarFeed(companyId, feed.id);
      notifySuccess('Kalenderlenken ble rotert.', { source: 'Kundeløp kalender' });
      await loadCalendarFeeds();
    } catch (err) {
      notifyError(err.message || 'Kunne ikke rotere kalenderlenken.', {
        title: 'Rotering feilet',
        source: 'Kundeløp kalender',
      });
    } finally {
      setFeedActionId(null);
    }
  }, [companyId, loadCalendarFeeds]);

  const deleteFeed = useCallback(async (feed) => {
    if (!companyId || !feed?.id) return;
    const confirmed = window.confirm('Slette denne kalenderlenken? URL-en slutter å virke.');
    if (!confirmed) return;
    setFeedActionId(feed.id);
    try {
      await DashboardApi.deleteCompanyInboxCalendarFeed(companyId, feed.id);
      notifySuccess('Kalenderlenken ble slettet.', { source: 'Kundeløp kalender' });
      if (editingFeedId === feed.id) closeFeedForm();
      await loadCalendarFeeds();
    } catch (err) {
      notifyError(err.message || 'Kunne ikke slette kalenderlenken.', {
        title: 'Sletting feilet',
        source: 'Kundeløp kalender',
      });
    } finally {
      setFeedActionId(null);
    }
  }, [closeFeedForm, companyId, editingFeedId, loadCalendarFeeds]);

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Kundeløp kalender</h1>
          <p>Velg et selskap for å se kalenderen.</p>
        </div>
      </div>
    );
  }

  const selectedEventKey = selectedEvent ? getEventKey(selectedEvent) : null;
  const inquiryAssignedId = selectedEvent?.inquiry_assigned_member?.account_id || null;

  return (
    <div className="page inquiry-calendar-page">
      <div className="page-header inquiry-calendar-header">
        <div>
          <p className="page-eyebrow">Henvendelser</p>
          <h1>Kundeløp kalender</h1>
          <p className="page-subtitle">
            Planlagte avtaler i kundeløp for produkter teamet har tilgang til.
          </p>
        </div>
      </div>

      <div className="inquiry-calendar-shell">
        <section className="inquiry-calendar-month" aria-label="Kundeløp månedskalender">
          <div className="inquiry-calendar-month-toolbar" aria-label="Kalenderfiltre">
            <div className="inquiry-calendar-month-title">
              <span>Måned</span>
              <strong>{monthLabel}</strong>
            </div>
            <div className="inquiry-calendar-month-filters">
              <label>
                <span>Produkt</span>
                <select
                  className="select select--sm"
                  value={botProductFilter}
                  onChange={(event) => setBotProductFilter(event.target.value)}
                  disabled={summaryLoading}
                >
                  <option value="">Alle produkter</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Ansvar</span>
                <select
                  className="select select--sm"
                  value={assignedFilter}
                  onChange={(event) => setAssignedFilter(event.target.value)}
                >
                  <option value="all">Alle avtaler</option>
                  <option value="me">Mine avtaler</option>
                </select>
              </label>
            </div>
            <div className="inquiry-calendar-month-actions">
              <button
                type="button"
                className="button button-secondary button-small"
                onClick={() => setMonthStart((current) => addMonths(current, -1))}
                aria-label="Forrige måned"
              >
                <FiChevronLeft aria-hidden="true" />
                Forrige
              </button>
              <button
                type="button"
                className="button button-secondary button-small"
                onClick={() => setMonthStart(getMonthStart(new Date()))}
              >
                Denne måneden
              </button>
              <button
                type="button"
                className="button button-secondary button-small"
                onClick={() => setMonthStart((current) => addMonths(current, 1))}
                aria-label="Neste måned"
              >
                Neste
                <FiChevronRight aria-hidden="true" />
              </button>
              <button
                type="button"
                className="button button-ghost button-small"
                onClick={loadEvents}
                disabled={loading}
              >
                <FiRefreshCw aria-hidden="true" />
                Oppdater
              </button>
            </div>
          </div>
          <div className="inquiry-calendar-weekday-row" aria-hidden="true">
            {Array.from({ length: 7 }, (_, index) => addDays(gridStart, index)).map((day) => (
              <span key={getLocalDateKey(day)}>{weekdayFormatter.format(day)}</span>
            ))}
          </div>
          <div className="inquiry-calendar-month-grid">
            {calendarDays.map((day) => {
              const dayKey = getLocalDateKey(day);
              const dayEvents = eventsByDay.get(dayKey) || [];
              const isToday = dayKey === getLocalDateKey(new Date());
              const isCurrentMonth = day.getMonth() === monthStart.getMonth();
              return (
                <div
                  key={dayKey}
                  className={[
                    'inquiry-calendar-day',
                    isToday ? 'is-today' : '',
                    !isCurrentMonth ? 'is-outside-month' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <header>
                    <span>{dayLabelFormatter.format(day)}</span>
                    <strong>{dayEvents.length}</strong>
                  </header>
                  <div className="inquiry-calendar-day-events">
                    {loading ? (
                      <p className="inquiry-calendar-empty">Laster…</p>
                    ) : dayEvents.length === 0 ? (
                      <p className="inquiry-calendar-empty">Ingen avtaler.</p>
                    ) : (
                      dayEvents.map((event) => (
                        <CalendarEventCard
                          key={getEventKey(event)}
                          event={event}
                          onOpen={openEvent}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="inquiry-calendar-upcoming" aria-label="Kommende avtaler">
          <header>
            <h2>Kommende avtaler</h2>
            <span>{upcomingEvents.length}</span>
          </header>
          <section className="inquiry-calendar-stats" aria-label="Kalenderstatus">
            <article>
              <span>Avtaler</span>
              <strong>{stats.total}</strong>
            </article>
            <article>
              <span>Planlagt</span>
              <strong>{stats.planned}</strong>
            </article>
            <article>
              <span>Ferdige</span>
              <strong>{stats.done}</strong>
            </article>
            <article>
              <span>Henvendelser</span>
              <strong>{stats.uniqueInquiries}</strong>
            </article>
          </section>

          <section className="inquiry-calendar-shared" aria-label="Delte kalendere">
            <header>
              <div>
                <h2>Delte kalendere</h2>
                <p>iCal-lenker for Google Calendar, Apple Calendar og Outlook.</p>
              </div>
              <button
                type="button"
                className="button button-secondary button-small"
                onClick={openCreateFeedForm}
              >
                <FiPlus aria-hidden="true" />
                Opprett lenke
              </button>
            </header>

            {feedFormOpen ? (
              <form className="inquiry-calendar-feed-form" onSubmit={handleSaveFeed}>
                <label>
                  <span>Navn</span>
                  <input
                    className="input"
                    type="text"
                    value={feedForm.name}
                    onChange={(event) => updateFeedForm({ name: event.target.value })}
                    maxLength={120}
                    disabled={feedSaving}
                  />
                </label>
                <label>
                  <span>Omfang</span>
                  <select
                    className="select"
                    value={feedForm.scope}
                    onChange={(event) => updateFeedForm({ scope: event.target.value })}
                    disabled={feedSaving}
                  >
                    <option value="assigned_to_me">Tildelt meg</option>
                    <option value="product">Produkt</option>
                  </select>
                </label>
                {feedForm.scope === 'product' ? (
                  <label>
                    <span>Produkt</span>
                    <select
                      className="select"
                      value={feedForm.botProductId}
                      onChange={(event) => updateFeedForm({ botProductId: event.target.value })}
                      disabled={feedSaving || summaryLoading}
                    >
                      <option value="">Velg produkt</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="inquiry-calendar-feed-enabled">
                  <input
                    type="checkbox"
                    checked={feedForm.enabled}
                    onChange={(event) => updateFeedForm({ enabled: event.target.checked })}
                    disabled={feedSaving}
                  />
                  <span>Aktiv lenke</span>
                </label>
                {hasGoogleServiceAccount ? (
                  <label className="inquiry-calendar-feed-enabled">
                    <input
                      type="checkbox"
                      checked={feedForm.excludeGoogleMeet}
                      onChange={(event) => updateFeedForm({ excludeGoogleMeet: event.target.checked })}
                      disabled={feedSaving}
                    />
                    <span>Ekskluder Teams/Meet-avtaler</span>
                  </label>
                ) : null}
                <footer>
                  <button
                    type="button"
                    className="button button-ghost button-small"
                    onClick={closeFeedForm}
                    disabled={feedSaving}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="button button-primary button-small"
                    disabled={feedSaving}
                  >
                    <FiSave aria-hidden="true" />
                    {feedSaving ? 'Lagrer…' : (editingFeedId ? 'Lagre' : 'Opprett')}
                  </button>
                </footer>
              </form>
            ) : null}

            {feedsLoading ? (
              <p className="inquiry-calendar-empty">Laster kalenderlenker…</p>
            ) : calendarFeeds.length === 0 ? (
              <p className="inquiry-calendar-empty">Ingen delte kalendere ennå.</p>
            ) : (
              <div className="inquiry-calendar-feed-list">
                {calendarFeeds.map((feed) => {
                  const feedUrl = getCalendarFeedUrl(feed);
                  const isBusy = feedActionId === feed.id;
                  return (
                    <article key={feed.id} className={!feed.enabled ? 'is-disabled' : ''}>
                      <div className="inquiry-calendar-feed-main">
                        <span className="inquiry-calendar-feed-icon" aria-hidden="true">
                          <FiLink />
                        </span>
                        <div>
                          <strong>{feed.name}</strong>
                          <small>{getFeedScopeLabel(feed)} · {feed.enabled ? 'Aktiv' : 'Deaktivert'}</small>
                        </div>
                      </div>
                      <input className="input" type="text" readOnly value={feedUrl} onFocus={(event) => event.target.select()} />
                      <div className="inquiry-calendar-feed-actions">
                        <button
                          type="button"
                          className="button button-ghost button-small"
                          onClick={() => copyFeedUrl(feed)}
                          disabled={isBusy}
                          aria-label="Kopier kalenderlenke"
                          title="Kopier"
                        >
                          <FiCopy aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="button button-ghost button-small"
                          onClick={() => openEditFeedForm(feed)}
                          disabled={isBusy}
                          aria-label="Rediger kalenderlenke"
                          title="Rediger"
                        >
                          <FiEdit2 aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="button button-ghost button-small"
                          onClick={() => rotateFeed(feed)}
                          disabled={isBusy}
                          aria-label="Roter kalenderlenke"
                          title="Roter"
                        >
                          <FiRotateCw aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="button button-ghost button-small"
                          onClick={() => deleteFeed(feed)}
                          disabled={isBusy}
                          aria-label="Slett kalenderlenke"
                          title="Slett"
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {upcomingEvents.length === 0 ? (
            <p className="inquiry-calendar-empty">Ingen kommende avtaler i synlig periode.</p>
          ) : (
            <div className="inquiry-calendar-upcoming-list">
              {upcomingEvents.map((event) => (
                <button
                  key={getEventKey(event)}
                  type="button"
                  className="inquiry-calendar-upcoming-item"
                  onClick={() => openEvent(event)}
                >
                  <span>{formatDateTime(getEventStart(event))}</span>
                  <strong>{event.step_label || event.step_key}</strong>
                  <small>{getInquiryName(event)} · {getAssigneeLabel(event)}</small>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>

      {selectedEvent ? (
        <div className="inquiry-calendar-modal-backdrop" role="presentation" onMouseDown={closeEvent}>
          <article
            className="inquiry-calendar-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inquiry-calendar-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p>{selectedEvent.product?.name || 'Produkt'}</p>
                <h2 id="inquiry-calendar-modal-title">{selectedEvent.step_label || selectedEvent.step_key}</h2>
                <Link to={`/henvendelser/${selectedEvent.inbox_id}`}>
                  {getInquiryName(selectedEvent)}
                  <FiExternalLink aria-hidden="true" />
                </Link>
              </div>
              <button
                type="button"
                className="button button-ghost button-small"
                onClick={closeEvent}
                aria-label="Lukk"
              >
                <FiX aria-hidden="true" />
              </button>
            </header>

            <div className="inquiry-calendar-modal-grid">
              <AppointmentDateTimeRangeField
                startValue={selectedDraft?.plannedStartAt || ''}
                endValue={selectedDraft?.plannedEndAt || ''}
                disabled={!canManage || savingKey === selectedEventKey}
                onChange={updateSelectedDraft}
              />
              <label>
                <span>Status</span>
                <select
                  className="select"
                  value={selectedDraft?.status || selectedEvent.status || 'planned'}
                  disabled={!canManage || savingKey === selectedEventKey}
                  onChange={(event) => updateSelectedDraft({ status: event.target.value })}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Ansvarlig</span>
                <select
                  className="select"
                  value={selectedDraft?.assignedAccountId || ''}
                  disabled={!canManage || savingKey === selectedEventKey}
                  onChange={(event) => updateSelectedDraft({ assignedAccountId: event.target.value })}
                >
                  <option value="">{selectedEvent.inquiry_assigned_member ? getAssigneeLabel({ assigned_member: selectedEvent.inquiry_assigned_member }) : 'Ufordelt'}</option>
                  {assignees
                    .filter((assignee) => assignee.account_id !== inquiryAssignedId)
                    .map((assignee) => (
                      <option key={assignee.account_id} value={assignee.account_id}>
                        {assignee.display_name || assignee.email}
                      </option>
                  ))}
                </select>
              </label>
              <fieldset className="inquiry-calendar-modal-attendance">
                <legend>Oppmøte</legend>
                <div className="inquiry-calendar-attendance-toggle">
                  <button
                    type="button"
                    className={selectedDraft?.attendanceType === 'digital' ? 'is-selected' : ''}
                    disabled={!canManage || savingKey === selectedEventKey}
                    onClick={() => updateSelectedDraft({ attendanceType: 'digital' })}
                  >
                    Digitalt
                  </button>
                  <button
                    type="button"
                    className={selectedDraft?.attendanceType === 'physical' ? 'is-selected' : ''}
                    disabled={!canManage || savingKey === selectedEventKey}
                    onClick={() => updateSelectedDraft({ attendanceType: 'physical' })}
                  >
                    Fysisk
                  </button>
                </div>
              </fieldset>
              {selectedDraft?.attendanceType === 'digital' ? (
                <>
                  <GoogleMeetSection
                    draft={selectedDraft || {}}
                    onUpdateDraft={updateSelectedDraft}
                    companyId={companyId}
                    disabled={!canManage || savingKey === selectedEventKey}
                    saving={savingKey === selectedEventKey}
                    assigneeEmail={selectedEvent?.assigned_member?.email || ''}
                  />
                  {!selectedDraft?.useGoogleMeet ? (
                    <label className="is-full-width">
                      <span>Lenke</span>
                      <input
                        className="input"
                        type="url"
                        value={selectedDraft?.meetingUrl || ''}
                        disabled={!canManage || savingKey === selectedEventKey}
                        onChange={(event) => updateSelectedDraft({ meetingUrl: event.target.value })}
                        placeholder="https://"
                      />
                    </label>
                  ) : null}
                </>
              ) : null}
              {selectedDraft?.attendanceType === 'physical' ? (
                <label className="is-full-width">
                  <span>Adresse</span>
                  <LocationInput
                    value={selectedDraft?.locationValue || selectedDraft?.locationAddress || ''}
                    disabled={!canManage || savingKey === selectedEventKey}
                    onChange={(nextValue) => updateSelectedDraft({
                      locationValue: nextValue,
                      locationAddress: getLocationAddress(nextValue),
                    })}
                    aria-label="Adresse"
                  />
                </label>
              ) : null}
              <label className="is-full-width">
                <span>Kommentar</span>
                <textarea
                  className="input inquiries-note-textarea"
                  value={selectedDraft?.comment || ''}
                  disabled={!canManage || savingKey === selectedEventKey}
                  onChange={(event) => updateSelectedDraft({ comment: event.target.value })}
                />
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="button button-secondary"
                onClick={closeEvent}
                disabled={savingKey === selectedEventKey}
              >
                Lukk
              </button>
              {canManage ? (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleSaveEvent}
                  disabled={savingKey === selectedEventKey}
                >
                  <FiSave aria-hidden="true" />
                  {savingKey === selectedEventKey ? 'Lagrer…' : 'Lagre endringer'}
                </button>
              ) : null}
            </footer>
          </article>
        </div>
      ) : null}
    </div>
  );
};

export default InquiryCalendarPage;
